// apps/web/src/ui/UnreadBadge.tsx
import { useTranslation } from 'react-i18next';

/**
 * @aiPurpose request-list unread indicator (#570). One of three visual
 *   states based on `{unreadCount, isMarkedUnread}`:
 *   - `unreadCount > 0` → red numeric badge (`99+` cap at display).
 *   - `unreadCount === 0 && isMarkedUnread === true` → red dot.
 *   - `unreadCount === 0 && isMarkedUnread === false` → null (no element).
 *
 *   The component is intentionally pure-presentational so the same
 *   element can sit inside the desktop `ListRow` name cell and the
 *   mobile `RequestMobileCard` name area without per-host props.
 * @aiInvariant uses semantic design tokens only — `--status-error-fg`
 *   (the "dusty red" used by the request status pill for RETURN /
 *   REJECTED) and `--fg-invert` for badge text. Never hardcoded hex.
 *   `aria-label` is localized through the dedicated `unreadBadge`
 *   namespace; an accessible-name override pairs its count key with
 *   a distinct key for the visible `99+` cap.
 */
const UNREAD_DISPLAY_CAP = 99;

export interface UnreadBadgeProps {
  unreadCount: number;
  isMarkedUnread: boolean;
  accessibleNameKeys?: {
    count: 'attentionCount';
    capped: 'attentionCapped';
  };
}

export function UnreadBadge({
  unreadCount,
  isMarkedUnread,
  accessibleNameKeys,
}: UnreadBadgeProps): JSX.Element | null {
  const { t } = useTranslation('unreadBadge');
  if (unreadCount > 0) {
    const capped = unreadCount > UNREAD_DISPLAY_CAP;
    const display = capped ? `${UNREAD_DISPLAY_CAP}+` : String(unreadCount);
    const ariaLabel = capped
      ? t(accessibleNameKeys?.capped ?? 'capped', { count: UNREAD_DISPLAY_CAP })
      : t(accessibleNameKeys?.count ?? 'count', { count: unreadCount });
    return (
      <span
        role="status"
        aria-label={ariaLabel}
        data-testid="unread-badge-count"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 18,
          height: 18,
          padding: '0 6px',
          borderRadius: 'var(--r-full)',
          background: 'var(--status-error-fg)',
          color: 'var(--fg-invert)',
          fontSize: 'var(--fz-11)',
          fontWeight: 600,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {display}
      </span>
    );
  }
  if (isMarkedUnread) {
    return (
      <span
        role="status"
        aria-label={t('markedUnread')}
        data-testid="unread-badge-dot"
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 'var(--r-full)',
          background: 'var(--status-error-fg)',
          flexShrink: 0,
        }}
      />
    );
  }
  return null;
}
